import React, {
    FC,
    HTMLAttributes,
    ReactNode, useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/RadioGroup';
import { Badge } from '@/components/ui/Badge';
import { Input, inputVariants } from '@/components/ui/Input';
import { FilterIconSvg, PureSearchSvg, XIconSvg, ChevronLeftSvg, CloseSquareIcon } from '@/assets/Icons';
import { ArrowRightIconSvg } from '@/assets/Icons';
import { useSearchParams } from 'next/navigation';
import useSearch from '@/app/search/services/useSearch';
import { SelectFilterItem } from '@/app/search/models/filters';
import cloneDeep from 'lodash.clonedeep';
import useStore from '@/services/store/useStore';

interface BoxProps extends HTMLAttributes<HTMLDivElement> {
    className?: string;
    children: ReactNode;
}

const Box: FC<BoxProps> = ({ className = '', children, ...rest }) => {
    return (
        <div
            className={`border border-neutral-300 p-5 rounded-xl text-sm font-bold cursor-pointer ${className}`}
            {...rest}
        >
            {children}
        </div>
    );
};

const Filters: FC = () => {
    // TODO: clean the component
    // TODO: remove unnecessary updating of filters (setFilters)
    const { searchFilters } = useSearch();

    const searchParams = useSearchParams();
    const { appliedFilters, setAppliedFilters } = useStore(state => state);
    const [filters, setFilters] = useState({});
    const [filterDialog, setFilterDialog] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [singleFilterDialog, setSingleFilterDialog] = useState(false);
    const [selectedFilterData, setSelectedFilterData] = useState(null);

    /* for non-ctype filters */
    const checkIfFilterIsSelected = useCallback((items, key) => appliedFilters[key] &&
        !(items?.some(item => {
            return (String(item.value) === appliedFilters[key] && item.is_all);
        })), [appliedFilters]);

    const numOfAppliedFilters = useMemo(() => Object.keys(appliedFilters).reduce((count, currentKey) => {
        return count + (currentKey === 'ctype' ? appliedFilters[currentKey].size :
            searchFilters?.data?.children?.find(filter => filter.key === currentKey && checkIfFilterIsSelected(filter?.children?.items, filter.key)) ? 1 : 0);
    }, 0), [appliedFilters]);

    const orderedSearchFilters = useMemo(() => searchFilters?.data?.children?.reduce((previousData, filterItem) => {
        if (filterItem.key === 'ctype') {
            return [...previousData, ...filterItem?.children?.items.map((item, index) => ({
                ...filterItem,
                children: {
                    ...filterItem.children,
                    items: [filterItem.children.items[index]]
                }
            }))];
        } else {
            return [...previousData, filterItem];
        }
    }, []).reduce((prev, current) => {
        const isSelected = (current.key === 'ctype' && appliedFilters['ctype']?.has(current?.children?.items?.[0]?.value)) ||
            (current.key !== 'ctype' && checkIfFilterIsSelected(current?.children?.items, current.key));

        return isSelected ? [current, ...prev] : [...prev, current];
    }, []), [searchFilters, appliedFilters]);

    useEffect(() => {
        setFilters(cloneDeep(appliedFilters));
    }, [appliedFilters]);

    const handleCheckboxCheck = (checked: boolean | string) => {
        if (checked) {
            setFilters((prevState) => ({
                ...prevState
            }));
        } else {
            setFilters((prevState) => ({
                ...prevState
            }));
        }
    };

    const handleInputFilterChange = (e, selectedFilterData, index: number) => {
        setFilters((prevState) => {
            const {
                [selectedFilterData.children.items[index].name]: _,
                ...restInputFilterStates
            } = prevState[selectedFilterData.key] || {};

            if (!Object.keys(restInputFilterStates).length && !e.target.value) {
                const { [selectedFilterData.key]: _, ...restStates } = prevState;
                return restStates;
            }

            return {
                ...prevState,
                [selectedFilterData.key]: {
                    ...(restInputFilterStates || {}),
                    ...(e.target.value ? { [selectedFilterData.children.items[index].name]: e.target.value } : {})
                }
            };
        });
    };

    return (
        <div
            className="flex shrink-0 z-[35] absolute overflow-auto w-full gap-2 hide-scroll top-[71px] max-w-[450px] py-6 px-4 bg-white">
            <Dialog
                open={filterDialog}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setFilters(cloneDeep(appliedFilters));
                    }
                    setFilterDialog(isOpen);
                }}
            >
                <DialogTrigger asChild>
                    <Button variant={!!numOfAppliedFilters ? 'primaryBlue' : 'surface'} size="sm">
                        <FilterIconSvg className="text-background-on" />
                        <span className="me-[1px]">
                            {!!numOfAppliedFilters && numOfAppliedFilters}
                        </span>
                        فیلتر
                    </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none sm:rounded-none top-0 gap-0 p-0">
                    <DialogTitle className="hidden">فیلتر</DialogTitle>
                    <DialogDescription className="hidden">فیلتر های جستجو درسا</DialogDescription>
                    <DialogHeader className="flex-row px-4 py-6 shadow-[0px_4px_12px_0px_rgba(0,_0,_0,_0.03)]">
                        <DialogClose className="ml-6">
                            <ArrowRightIconSvg />
                            <span className="sr-only">بستن</span>
                        </DialogClose>
                        <DialogTitle>فیلتر‌ها</DialogTitle>
                    </DialogHeader>
                    <div
                        className="py-6 flex flex-col gap-4 overflow-auto h-[calc(100%-176px)] px-6 pb-8 mb-[107px] pt-6">
                        {searchFilters?.data.children.map((filter) => (
                            (filter.key === 'ctype') ?
                                <Box
                                    key={filter.key}
                                >
                                    <h4 className="mb-3">
                                        {filter.title}
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {
                                            filter.children.items.map(filterItem =>
                                                <Button
                                                    key={filterItem.value}
                                                    variant={(filters['ctype']?.has(filterItem.value)) ? 'primaryBlue' : 'surface'}
                                                    size="sm"
                                                    onClick={() => {
                                                        // TODO: write this part better, may be a separate component
                                                        if ((filters['ctype']?.has(filterItem.value))) {
                                                            // if was previously selected
                                                            const { ctype: ctypeFilter, ...restOfTheFilters } = filters;
                                                            const newFilterItem = new Set(ctypeFilter);
                                                            newFilterItem.delete(filterItem.value);
                                                            if (newFilterItem)
                                                                setFilters({
                                                                    ...restOfTheFilters,
                                                                    ...(newFilterItem.size ? { ['ctype']: newFilterItem } : {})
                                                                });

                                                            return;
                                                        }

                                                        setFilters((prevState) => ({
                                                            ...prevState,
                                                            ['ctype']: prevState['ctype'] ? prevState['ctype'].add(filterItem.value) : new Set([filterItem.value])
                                                        }));
                                                    }}
                                                >
                                                    {filterItem.label}
                                                </Button>
                                            )
                                        }
                                    </div>
                                </Box>
                                :
                                <Box
                                    key={filter.key}
                                    className="flex items-center justify-between"
                                    onClick={() => {
                                        setSelectedFilterData(filter);
                                        setSingleFilterDialog(true);
                                    }}
                                >
                                    <h4 className="flex items-center gap-2">
                                        <img src={filter.icon} className="w-6 h-6" />
                                        {filter.title}
                                    </h4>
                                    <ChevronLeftSvg className="text-background-on h-6 w-6 opacity-70" />
                                </Box>
                        ))}
                    </div>
                    <DialogFooter
                        className="fixed bottom-0 left-0 right-0 p-6 bg-white gap-2.5 shadow-[4px_0px_12px_0px_rgba(0,_0,_0,_0.03)]">
                        <Button
                            className="w-full"
                            size="lg"
                            onClick={() => {
                                setAppliedFilters(cloneDeep(filters));
                                setFilterDialog(false);
                            }}
                        >
                            اعمال فیلتر
                        </Button>
                        <Button
                            className="min-w-[40%] w-[40%] text-primary"
                            size="lg"
                            variant="surface"
                            onClick={() => {
                                setFilters({});
                                setAppliedFilters({});
                            }}
                        >
                            حذف فیلتر
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {orderedSearchFilters?.map((filter) => (
                /* exclude ctype filter since each item in it should be shown as chip */
                (filter.key === 'ctype') ?
                    filter.children.items.map((filterItem: SelectFilterItem) =>
                        <Button
                            key={filterItem.value}
                            variant={(appliedFilters['ctype']?.has(filterItem.value)) ? 'primaryBlue' : 'surface'}
                            size="sm"
                            onClick={() => {
                                // TODO: write this part better, may be a separate component
                                if ((appliedFilters['ctype']?.has(filterItem.value))) {
                                    // if was previously selected
                                    const { ctype: ctypeFilter, ...restOfTheFilters } = filters;
                                    const newFilterItem = new Set(ctypeFilter);
                                    newFilterItem.delete(filterItem.value);
                                    setFilters({
                                        ...restOfTheFilters,
                                        ...(newFilterItem.size ? { ['ctype']: newFilterItem } : {})
                                    });
                                    setAppliedFilters({
                                        ...restOfTheFilters,
                                        ...(newFilterItem.size ? { ['ctype']: newFilterItem } : {})
                                    });

                                    return;
                                }

                                setFilters((prevState) => ({
                                    ...prevState,
                                    ['ctype']: prevState['ctype'] ? prevState['ctype'].add(filterItem.value) : new Set([filterItem.value])
                                }));

                                setAppliedFilters({
                                    ...appliedFilters,
                                    ['ctype']: appliedFilters['ctype'] ? appliedFilters['ctype'].add(filterItem.value) : new Set([filterItem.value])
                                });
                            }}
                        >
                            {filterItem.label}
                            {
                                (appliedFilters['ctype']?.has(filterItem.value)) &&
                                <CloseSquareIcon className="text-[#1D3557]" />
                            }
                        </Button>) :
                    <Button
                        key={filter.key}
                        variant={checkIfFilterIsSelected(filter?.children?.items, filter.key) ? 'primaryBlue' : 'surface'}
                        size="sm"
                        onClick={() => {
                            setSelectedFilterData(filter);
                            setSingleFilterDialog(true);
                        }}
                    >
                        {
                            (filter?.children?.type === 'radio' && checkIfFilterIsSelected(filter?.children?.items, filter.key) && filter?.children?.items.find(item => (item.value === appliedFilters[filter.key]))?.label)
                            || filter.title
                        }
                    </Button>
            ))}
            <Dialog
                open={singleFilterDialog}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setFilters(cloneDeep(appliedFilters));
                    }
                    setSingleFilterDialog(isOpen);
                }}
            >
                <DialogContent
                    className={`gap-0 p-0 ${
                        selectedFilterData?.searchable ? 'rounded-none sm:rounded-none h-full' : ''
                    }`}
                >
                    <DialogTitle className="hidden">فیلتر</DialogTitle>
                    <DialogDescription className="hidden">فیلتر های جستجو درسا</DialogDescription>
                    <div
                        className={`w-full flex justify-between items-center px-4 py-6 gap-6 ${
                            selectedFilterData?.searchable ? ' shadow-[0px_4px_12px_0px_rgba(0,_0,_0,_0.03)]' : ''
                        }`}
                    >
                        {!isSearching ? (
                            <>
                <span className="flex items-center gap-6 font-bold text-base">
                  {selectedFilterData?.searchable && (
                      <ArrowRightIconSvg
                          onClick={() => {
                              setFilters(cloneDeep(appliedFilters));
                              setSingleFilterDialog(false);
                          }}
                      />
                  )}
                    {selectedFilterData?.title}
                </span>
                                {selectedFilterData?.searchable && (
                                    <PureSearchSvg onClick={() => setIsSearching(true)} />
                                )}
                            </>
                        ) : (
                            <div className="flex items-center">
                                <ArrowRightIconSvg
                                    onClick={() => {
                                        setIsSearching(false);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={`overflow-auto h-[calc(100%-176px)] px-6 pb-8 mb-[107px] ${
                            selectedFilterData?.searchable ? 'pt-6' : ''
                        }`}
                    >
                        {selectedFilterData?.children.type === 'radio' && (
                            <RadioGroup
                                className="flex flex-col gap-3"
                                defaultValue={selectedFilterData?.children.items?.find(item => item.is_default)?.value}
                                value={filters[selectedFilterData?.key] || String(selectedFilterData?.children.items?.find(item => item.is_default)?.value)}
                                onValueChange={(value) => {
                                    if (value === String(selectedFilterData?.children.items?.find(item => item.is_default && !item.value)?.value)) {
                                        /* remove from filters if it was the default value */
                                        const { [selectedFilterData.key]: _, ...newFilters } = filters;
                                        setFilters(newFilters);
                                    } else {
                                        setFilters((prevState) => ({
                                            ...prevState,
                                            [selectedFilterData.key]: value
                                        }));
                                    }
                                }}
                                dir="rtl"
                            >
                                {selectedFilterData.children.items?.map((item) => (
                                    <div key={item.value} className="flex items-center gap-2 px-4 py-3">
                                        <RadioGroupItem value={String(item.value)} id={item.value || '_'} />
                                        <Label htmlFor={item.value || '_'}>{item.label}</Label>
                                    </div>
                                ))}
                            </RadioGroup>
                        )}
                        {selectedFilterData?.children.type === 'checkbox' && (
                            <>
                                {Array.isArray(filters[selectedFilterData.id]) &&
                                    !!(filters[selectedFilterData.id] as FilterItem[])?.length && (
                                        <div className="flex items-center gap-2 overflow-auto w-full hide-scroll mb-6">
                                            {(filters[selectedFilterData.id] as FilterItem[]).map(
                                                (filter: FilterItem) => (
                                                    <Badge key={filter.id} className="gap-2" variant="primaryBlue">
                                                        {filter?.title}
                                                        <XIconSvg
                                                            onClick={() =>
                                                                setFilters((prevState) => ({
                                                                    ...prevState,
                                                                    [selectedFilterData.id]: (
                                                                        prevState[selectedFilterData.id] as FilterItem[]
                                                                    ).filter(({ value }) => filter.value !== value)
                                                                }))
                                                            }
                                                        />
                                                    </Badge>
                                                )
                                            )}
                                        </div>
                                    )}
                                <div className="flex flex-col gap-4 px-4">
                                    {selectedFilterData?.items?.map((item: FilterItem) => (
                                        <div key={item.id} className="flex items-center gap-2 px-4 py-3">
                                            <Checkbox
                                                id={String(item.id)}
                                                checked={(filters[selectedFilterData.id] as FilterItem[])
                                                    ?.map(({ value }) => value)
                                                    ?.includes(item.value)}
                                                onCheckedChange={(checked) => {
                                                    return checked
                                                        ? setFilters((prevState) => {
                                                            return {
                                                                ...prevState,
                                                                [selectedFilterData.id]: !prevState[selectedFilterData.id]
                                                                    ? [item]
                                                                    : [
                                                                        ...((prevState[selectedFilterData.id] as FilterItem[]) ||
                                                                            []),
                                                                        item
                                                                    ]
                                                            };
                                                        })
                                                        : setFilters((prevState) => ({
                                                            ...prevState,
                                                            [selectedFilterData.id]: !prevState[selectedFilterData.id]
                                                                ? []
                                                                : [
                                                                    ...((prevState[selectedFilterData.id] as FilterItem[]) || [])
                                                                ].filter(({ value }) => item.value !== value)
                                                        }));
                                                }}
                                            />
                                            <Label htmlFor={item.id}>{item.title}</Label>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {selectedFilterData?.children.type === 'input' && (
                            <div className="py-4 flex flex-col gap-3">
                                <div className="flex items-center gap-2 font-bold text-sm text-surface-on">
                                    <img src={selectedFilterData?.icon} className="w-6 h-6" />
                                    {selectedFilterData.title}
                                </div>
                                <div className={inputVariants}>
                                    <Input
                                        unStyled
                                        type={selectedFilterData?.children?.items?.[0].type}
                                        className="w-full px-3"
                                        placeholder={selectedFilterData?.children?.items?.[0].label}
                                        value={(filters[selectedFilterData.key] as FilterItem)?.from || ''}
                                        onChange={(e) => handleInputFilterChange(e, selectedFilterData, 0)}
                                    />
                                    <span className="w-px h-6 bg-[#C5C7C9]" />
                                    <Input
                                        unStyled
                                        type={selectedFilterData?.children?.items?.[1].type}
                                        className="w-full px-3"
                                        placeholder={selectedFilterData?.children?.items?.[1].label}
                                        value={(filters[selectedFilterData.key] as FilterItem)?.to || ''}
                                        onChange={(e) => handleInputFilterChange(e, selectedFilterData, 1)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter
                        className="fixed bottom-0 left-0 right-0 p-6 bg-white gap-2.5 shadow-[4px_0px_12px_0px_rgba(0,_0,_0,_0.03)]">
                        {selectedFilterData?.type === 'radio' ? (
                            <Button
                                className="w-full"
                                size="lg"
                                onClick={() => {
                                    if (!filterDialog) setAppliedFilters(cloneDeep(filters));
                                    setSingleFilterDialog(false);
                                }}
                            >
                                تایید
                            </Button>
                        ) : (
                            <>
                                <Button
                                    className="w-full"
                                    size="lg"
                                    onClick={() => {
                                        if (!filterDialog) {
                                            setAppliedFilters(cloneDeep(filters));
                                        }
                                        setSingleFilterDialog(false);
                                    }}
                                >
                                    اعمال فیلتر
                                </Button>
                                <Button
                                    className="min-w-[40%] w-[40%] text-primary"
                                    size="lg"
                                    variant="surface"
                                    onClick={() => {
                                        // clear selected filter, fallback to default value if present
                                        // if item.value is falsy, by convention between back and front, we don't send it as query param,
                                        // so clear it from filters
                                        const defaultItem = selectedFilterData?.children?.items?.find(item => item.is_default && item.value);
                                        if (defaultItem) {
                                            setFilters({ ...filters, [selectedFilterData.key]: defaultItem.value });
                                        } else {
                                            const { [selectedFilterData.key]: _, ...newFilters } = filters;
                                            setFilters(newFilters);
                                        }
                                    }}
                                >
                                    حذف همه
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Filters;